import { createFileRoute, notFound, Outlet } from "@tanstack/react-router";
import { getAthleteProfile } from "api-client";

import { loaderHandler } from "src/lib/loaderHandler";
import { AthletePageLayout } from "src/pages/AthletePageLayout";
import { CoachAthleteNotFoundPage } from "src/pages/CoachAthleteNotFoundPage";
import { athleteProfileQueryOptions } from "src/queries/athletes";

export const Route = createFileRoute("/_auth/_coach/athletes/$athleteId")({
  component: RoutePage,
  notFoundComponent: CoachAthleteNotFoundPage,
  ssr: true,
  shouldReload: false,
  async beforeLoad({ context, params }) {
    const { queryKey } = athleteProfileQueryOptions(params.athleteId);
    const athleteProfile =
      context.queryClient.getQueryData(queryKey) ??
      (await fetchAthleteProfile(params.athleteId));
    context.queryClient.setQueryData(queryKey, athleteProfile);

    if (athleteProfile.athlete.partnership.status === "INACTIVE") {
      throw notFound({
        data: { archivedAthleteName: athleteProfile.athlete.user.name },
      });
    }

    return { athleteProfile };
  },
  async loader({ context, params }) {
    return await loaderHandler(async () => {
      const { athleteProfile } = context;

      return {
        data: {
          athleteProfile,
        },
        breadcrumb: {
          id: athleteProfile.athlete.userId,
          label: athleteProfile.athlete.user.name,
          href: `/athletes/${params.athleteId}/profile`,
          athleteSwitcher: {
            athleteId: params.athleteId,
          },
        },
      };
    });
  },
});

async function fetchAthleteProfile(athleteId: string) {
  const response = await getAthleteProfile({
    path: { athleteId },
    throwOnError: false,
  });

  if (response.response?.status === 404) {
    throw notFound();
  }

  if (response.error) {
    throw response.error;
  }

  return response.data.data;
}

function RoutePage() {
  const params = Route.useParams();

  return (
    <AthletePageLayout
      key={params.athleteId}
      config={{
        athleteId: params.athleteId,
        basePath: `/athletes/${params.athleteId}`,
        tabs: [
          {
            id: "profile",
            link: {
              to: "/athletes/$athleteId/profile",
              params: { athleteId: params.athleteId },
            },
          },
          {
            id: "metrics",
            link: {
              to: "/athletes/$athleteId/metrics",
              params: { athleteId: params.athleteId },
            },
          },
          {
            id: "calendar",
            link: {
              to: "/athletes/$athleteId/calendar",
              params: { athleteId: params.athleteId },
            },
          },
          {
            id: "programs",
            link: {
              to: "/athletes/$athleteId/programs",
              params: { athleteId: params.athleteId },
            },
          },
          {
            id: "statistics",
            link: {
              to: "/athletes/$athleteId/statistics",
              params: { athleteId: params.athleteId },
            },
          },
          {
            id: "questionnaires",
            link: {
              to: "/athletes/$athleteId/questionnaires",
              params: { athleteId: params.athleteId },
            },
          },
          {
            id: "payments",
            link: {
              to: "/athletes/$athleteId/payments",
              params: { athleteId: params.athleteId },
            },
          },
        ],
      }}
    >
      <Outlet />
    </AthletePageLayout>
  );
}
